import type { RenderContext, SectionProps } from "../context";
import { field } from "../context";
import { storeFromHref } from "../helpers";
import { Icon } from "../icons";
import { CARD_HOVER, Container, EditorImagePlaceholder, Img, MultiLine, OrderLink, Price, REVEAL, Rail, STACK, STAGGER, SectionHeader, SectionShell, SiteLink, cx } from "../ui";
import { STORE_ICONS } from "./links";

type Content = SectionProps<"picks">["node"]["content"];
type Item = Content["items"][number];

export const DEFAULT_DISCLOSURE = "Beberapa tautan di bawah adalah tautan afiliasi. Saya bisa mendapat komisi bila kamu membeli lewat tautan ini, tanpa biaya tambahan untukmu.";

function cta(c: Content, item: Item): string {
  if (c.linkLabel) return c.linkLabel;
  const name = item.store || storeFromHref(item.href).name;
  return name ? `Lihat di ${name}` : "Lihat";
}

type BentoSpan = "big" | "wide" | "full";

/**
 * Bento rhythm on a 4-column grid: each full group of 4 is big (2×2) + small + small + wide, which fills
 * two rows exactly. Leftovers fill the last row: 1 → full width, 2 → two wide, 3 → wide + two small.
 */
export function bentoSpan(i: number, n: number): BentoSpan | undefined {
  const start = i - (i % 4);
  const rest = n - start;
  if (rest >= 4) return i % 4 === 0 ? "big" : i % 4 === 3 ? "wide" : undefined;
  if (rest === 1) return "full";
  if (rest === 2) return "wide";
  return i === start ? "wide" : undefined;
}

function Card({ ctx, c, item, i, layout, feature }: { ctx: RenderContext; c: Content; item: Item; i: number; layout: "grid" | "list" | "carousel"; feature?: BentoSpan }) {
  const p = `content.items.${i}`;
  const affiliate = c.affiliate !== false;
  const store = storeFromHref(item.href);
  const storeName = item.store || store.name;
  const list = layout === "list";
  // A text-only list row reads better than an empty grey thumbnail (e.g. article links).
  const showMedia = !list || !!item.image || ctx.editor;
  const media = item.image ? (
    <Img
      ctx={ctx}
      image={item.image}
      sizes={list ? "112px" : "(min-width: 1024px) 320px, (min-width: 640px) 50vw, 100vw"}
      target={list ? 480 : 960}
      path={`${p}.image`}
      className="transition-transform duration-700 group-hover:scale-[1.05] motion-reduce:transition-none"
    />
  ) : ctx.editor ? (
    <EditorImagePlaceholder ctx={ctx} path={`${p}.image`} />
  ) : (
    <div aria-hidden="true" className="grid h-full w-full place-items-center bg-site-primary/8 text-site-primary/50">
      <Icon name={STORE_ICONS[store.kind]} className="size-8" />
    </div>
  );
  return (
    <article className={cx("group relative flex h-full overflow-hidden rounded-site-md border border-site-border bg-site-surface shadow-site-sm", list ? "flex-row items-center gap-4 p-3 @md:gap-5" : "flex-col", CARD_HOVER)}>
      {showMedia ? (
        <div
          className={cx(
            "relative shrink-0 overflow-hidden bg-site-bg",
            list ? "size-24 rounded-site-sm @md:size-28" : feature === "big" ? "aspect-[4/3] @4xl:aspect-auto @4xl:min-h-0 @4xl:flex-1" : feature === "wide" ? "aspect-[4/3] @4xl:aspect-[16/7]" : feature === "full" ? "aspect-[4/3] @4xl:aspect-[21/7]" : "aspect-[4/3]",
          )}
        >
          {media}
          {item.badge && !list ? (
            <span className="absolute top-3 left-3 rounded-full bg-site-primary px-2.5 py-1 text-xs font-semibold text-site-on-primary shadow-site-sm" {...field(ctx, `${p}.badge`)}>
              {item.badge}
            </span>
          ) : null}
        </div>
      ) : null}
      <div className={cx("flex min-w-0 flex-col", feature === "big" ? "flex-1 @4xl:flex-none" : "flex-1", list ? (showMedia ? "py-1 pr-2" : "px-3 py-2") : "p-5")}>
        {storeName ? <p className="text-xs font-semibold tracking-[0.12em] text-site-muted uppercase">{storeName}</p> : null}
        <h3 className={cx("baze-heading leading-snug", list ? "text-lg" : feature === "big" ? "mt-1 text-xl @4xl:text-3xl" : "mt-1 text-xl")} {...field(ctx, `${p}.name`)}>
          {item.name}
        </h3>
        <MultiLine ctx={ctx} text={item.description} path={`${p}.description`} className={cx("mt-1.5 text-sm leading-relaxed text-site-muted", list && "line-clamp-2")} />
        <div className={cx("mt-auto flex flex-wrap items-center justify-between gap-3", list ? "pt-2" : "pt-5")}>
          <Price value={item.price} note={item.priceNote} className="text-site-text" />
          <SiteLink
            ctx={ctx}
            href={item.href}
            trackLink
            sponsored={affiliate}
            fallback="none"
            ariaLabel={`${cta(c, item)}: ${item.name}`}
            className={cx(
              "inline-flex min-h-11 items-center gap-2 rounded-site-button bg-site-primary px-4 text-sm font-semibold text-site-on-primary shadow-site-sm transition hover:shadow-site-md motion-reduce:transition-none",
              // The whole card is clickable; the button keeps its own focus ring.
              "after:absolute after:inset-0 after:content-['']",
            )}
          >
            <span>{cta(c, item)}</span>
            <Icon name="arrow-up-right" className="size-4" />
          </SiteLink>
        </div>
      </div>
    </article>
  );
}

/** "bazepos.id" from "https://www.bazepos.id/id" — the visible part of a project link. */
function hostOf(href: string): string | null {
  try {
    return new URL(href).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/**
 * Portfolio rows: a framed screenshot beside the name, a status pill, and an outlined visit button,
 * alternating sides on wide screens. Items without a screenshot get a monogram panel, never an empty box.
 */
function ShowcaseRow({ ctx, c, item, i }: { ctx: RenderContext; c: Content; item: Item; i: number }) {
  const p = `content.items.${i}`;
  const affiliate = c.affiliate !== false;
  const host = hostOf(item.href);
  const label = c.linkLabel || "Kunjungi";
  const flip = i % 2 === 1;
  return (
    <li className={cx(REVEAL, "grid items-center gap-8 @4xl:grid-cols-[1.25fr_1fr] @4xl:gap-14")}>
      <div className={cx("relative aspect-[16/10] overflow-hidden rounded-site-lg border border-site-border bg-site-surface shadow-site-lg", flip && "@4xl:order-2")}>
        {item.image ? (
          <Img ctx={ctx} image={item.image} sizes="(min-width: 1024px) 640px, 100vw" path={`${p}.image`} className="transition-transform duration-700 hover:scale-[1.03] motion-reduce:transition-none" />
        ) : ctx.editor ? (
          <EditorImagePlaceholder ctx={ctx} path={`${p}.image`} />
        ) : (
          <div aria-hidden="true" className="baze-showcase-blank grid h-full w-full place-items-center">
            <span className="baze-heading grid size-20 place-items-center rounded-site-lg border border-site-border bg-site-bg text-4xl text-site-primary shadow-site-md">
              {item.name.trim().charAt(0).toUpperCase()}
            </span>
          </div>
        )}
      </div>
      <div className={cx("min-w-0", flip && "@4xl:order-1")}>
        {item.badge ? (
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-site-muted" {...field(ctx, `${p}.badge`)}>
            <span aria-hidden="true" className="relative flex size-2">
              <span className="absolute inset-0 animate-ping rounded-full bg-site-accent opacity-60 motion-reduce:hidden" />
              <span className="relative size-2 rounded-full bg-site-accent" />
            </span>
            {item.badge}
          </span>
        ) : null}
        <h3 className="baze-heading mt-3 text-3xl leading-tight @3xl:text-4xl" {...field(ctx, `${p}.name`)}>
          {item.name}
        </h3>
        {item.store ? (
          <p className="mt-2 font-medium text-site-primary" {...field(ctx, `${p}.store`)}>
            {item.store}
          </p>
        ) : null}
        <MultiLine ctx={ctx} text={item.description} path={`${p}.description`} className="mt-4 leading-relaxed text-site-muted" />
        <Price value={item.price} note={item.priceNote} className="mt-4 text-site-text" />
        <SiteLink
          ctx={ctx}
          href={item.href}
          trackLink
          sponsored={affiliate}
          fallback="none"
          ariaLabel={`${label}: ${item.name}`}
          className="mt-6 inline-flex min-h-11 items-center gap-2 rounded-site-button border border-site-border px-4 text-sm font-semibold text-site-text transition hover:border-site-primary hover:text-site-primary motion-reduce:transition-none"
        >
          <span>{host ? `${label} ${host}` : label}</span>
          <Icon name="arrow-up-right" className="size-4" />
        </SiteLink>
      </div>
    </li>
  );
}

const RESERVE = "Halo {{nama_usaha}}, saya mau reservasi di {{produk}}.";

/**
 * Full-bleed photo panel per item (outlets of one estate, venues of one group): index, status pill, name
 * with an italic sub-name, copy, a WhatsApp reservation and a detail link. Copy alternates sides.
 */
function PanoramaPanel({ ctx, c, item, i }: { ctx: RenderContext; c: Content; item: Item; i: number }) {
  const p = `content.items.${i}`;
  const affiliate = c.affiliate !== false;
  const label = c.linkLabel || "Lihat detail";
  const right = i % 2 === 1;
  const button = "inline-flex min-h-11 items-center gap-2 rounded-site-button px-5 text-sm font-semibold tracking-[0.12em] uppercase transition motion-reduce:transition-none";
  return (
    <li className="relative isolate flex min-h-[32rem] overflow-hidden bg-site-inverted @4xl:min-h-[38rem]">
      <div className="absolute inset-0 -z-10">
        {item.image ? (
          <Img ctx={ctx} image={item.image} sizes="100vw" target={1600} path={`${p}.image`} className="transition-transform duration-[1.5s] hover:scale-[1.03] motion-reduce:transition-none" />
        ) : ctx.editor ? (
          <EditorImagePlaceholder ctx={ctx} path={`${p}.image`} />
        ) : (
          <div aria-hidden="true" className="baze-showcase-blank h-full w-full opacity-20" />
        )}
      </div>
      <div aria-hidden="true" className={cx("absolute inset-0 -z-10 bg-linear-to-t from-black/85 via-black/45 to-black/10", right ? "@4xl:bg-linear-to-l" : "@4xl:bg-linear-to-r", "@4xl:from-black/80 @4xl:via-black/40 @4xl:to-transparent")} />
      <Container className={cx("flex items-end py-12 @4xl:items-center @4xl:py-20", right && "@4xl:justify-end")}>
        <div className={cx(REVEAL, "max-w-xl text-white")}>
          <div className="flex items-center gap-4">
            <span aria-hidden="true" className="text-sm font-semibold tracking-[0.2em] text-white/70 tabular-nums">
              {String(i + 1).padStart(2, "0")}
            </span>
            {item.badge ? (
              <span className="rounded-full border border-white/35 px-3 py-1 text-[0.6875rem] font-semibold tracking-[0.2em] text-white/90 uppercase" {...field(ctx, `${p}.badge`)}>
                {item.badge}
              </span>
            ) : null}
          </div>
          <h3 className="baze-heading mt-5 text-4xl leading-tight @3xl:text-6xl" {...field(ctx, `${p}.name`)}>
            {item.name}
          </h3>
          {item.store ? (
            <p className="mt-1 font-site-heading text-2xl text-white/85 normal-case italic @3xl:text-3xl" {...field(ctx, `${p}.store`)}>
              {item.store}
            </p>
          ) : null}
          <MultiLine ctx={ctx} text={item.description} path={`${p}.description`} className="mt-5 leading-relaxed text-white/85" />
          <Price value={item.price} note={item.priceNote} className="mt-4 text-white" />
          <div className="mt-8 flex flex-wrap gap-3">
            <OrderLink ctx={ctx} message={RESERVE} produk={item.name} label="Reservasi" className={cx(button, "bg-site-primary text-site-on-primary shadow-site-md hover:shadow-site-lg")} />
            <SiteLink
              ctx={ctx}
              href={item.href}
              trackLink
              sponsored={affiliate}
              fallback="none"
              ariaLabel={`${label}: ${item.name}`}
              className={cx(button, "border border-white/50 text-white hover:bg-white hover:text-black")}
            >
              <span>{label}</span>
              <Icon name="arrow-up-right" className="size-4" />
            </SiteLink>
          </div>
        </div>
      </Container>
    </li>
  );
}

/**
 * Recommended products with outbound links (Shopee, TikTok Shop, Tokopedia …) or project cards.
 * Affiliate by default: links get rel="sponsored nofollow" and a visible disclosure line.
 */
export function Picks({ node, ctx }: SectionProps<"picks">) {
  const c = node.content;
  const affiliate = c.affiliate !== false;
  const layout = node.variant === "list" ? "list" : node.variant === "carousel" ? "carousel" : "grid";
  const bento = node.variant === "bento";
  const disclosure = affiliate ? (
    <p className="mt-4 inline-flex items-start gap-2 rounded-site-sm border border-site-border bg-site-surface px-3 py-2 text-xs leading-relaxed text-site-muted" {...field(ctx, "content.disclosure")}>
      <Icon name="link" className="mt-0.5 size-3.5 shrink-0" />
      <span>{c.disclosure || DEFAULT_DISCLOSURE}</span>
    </p>
  ) : null;
  const header = (
    <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description}>
      {disclosure}
    </SectionHeader>
  );

  if (node.variant === "panorama") {
    return (
      <SectionShell ctx={ctx} node={node} padded={false}>
        {c.title || c.eyebrow || c.description ? (
          <Container className="py-(--site-section-y) @3xl:py-(--site-section-y-lg)">{header}</Container>
        ) : null}
        <ul className="grid gap-px">
          {c.items.map((item, i) => (
            <PanoramaPanel key={i} ctx={ctx} c={c} item={item} i={i} />
          ))}
        </ul>
        {c.items.length === 0 && ctx.editor ? (
          <Container className="pb-12">
            <p className="rounded-site-md border border-dashed border-site-border p-8 text-center text-sm text-site-muted">Tambahkan brand, outlet, atau tempat.</p>
          </Container>
        ) : null}
      </SectionShell>
    );
  }

  if (node.variant === "showcase") {
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container>
          {header}
          <ul className={cx(STACK, "grid gap-16 @3xl:gap-24")}>
            {c.items.map((item, i) => (
              <ShowcaseRow key={i} ctx={ctx} c={c} item={item} i={i} />
            ))}
          </ul>
          {c.items.length === 0 && ctx.editor ? <p className={cx(STACK, "rounded-site-md border border-dashed border-site-border p-8 text-center text-sm text-site-muted")}>Tambahkan produk atau proyek.</p> : null}
        </Container>
      </SectionShell>
    );
  }

  if (layout === "carousel" && c.items.length > 0) {
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container>
          {header}
          <div className={STACK}>
            <Rail label={`Rekomendasi: ${c.title}`}>
              {c.items.map((item, i) => (
                <li key={i} className="w-[78%] shrink-0 snap-start py-1 @md:w-[46%] @3xl:w-[32%] @5xl:w-[25%]">
                  <Card ctx={ctx} c={c} item={item} i={i} layout="carousel" />
                </li>
              ))}
            </Rail>
          </div>
        </Container>
      </SectionShell>
    );
  }

  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        {header}
        <ul
          className={cx(
            STACK,
            STAGGER,
            layout === "list" ? "mx-auto grid max-w-3xl gap-4" : bento ? "grid grid-flow-dense gap-4 @xl:grid-cols-2 @4xl:grid-cols-4 @3xl:gap-5" : "grid gap-5 @xl:grid-cols-2 @4xl:grid-cols-3 @3xl:gap-6",
          )}
        >
          {c.items.map((item, i) => {
            const span = bento ? bentoSpan(i, c.items.length) : undefined;
            return (
              <li key={i} className={cx(span === "big" && "@xl:col-span-2 @4xl:row-span-2", span === "wide" && "@4xl:col-span-2", span === "full" && "@xl:col-span-2 @4xl:col-span-4")}>
                <Card ctx={ctx} c={c} item={item} i={i} layout={layout} feature={span} />
              </li>
            );
          })}
        </ul>
        {c.items.length === 0 && ctx.editor ? <p className={cx(STACK, "rounded-site-md border border-dashed border-site-border p-8 text-center text-sm text-site-muted")}>Tambahkan produk atau proyek.</p> : null}
      </Container>
    </SectionShell>
  );
}
